import React, { useState, useRef, useEffect } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Tooltip as UiTooltip } from '../../../../Tooltip';
import styles from "./pieChartCard.module.css";
// import ChartDownloadIcon from '../../../../../assets/chartDownloadIcon.svg';
import DownIcon from "../../../../../assets/DownIcon";

export interface PieChartItem {
  name: string;
  total_count: number;
  query_count: number;
  color: string;
}

const METRICS = [
  { label: "Audience Volume", key: "total_count" as const },
  { label: "Event Distribution", key: "query_count" as const },
];

interface PieChartCardProps {
  title: string;
  subtitle?: string;
  items: PieChartItem[];
  footerText?: string;
  isLoading?: boolean;
  hasError?: boolean;
  /** Called when user clicks a pie segment; passes the raw event_type key */
  onSegmentClick?: (eventType: string) => void;
}

const formatCount = (n: number): string => {
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
};

// Map display name → raw event_type key
const NAME_TO_EVENT_TYPE: Record<string, string> = {
  Query: "query",
  "Marked/Engaged": "marked_engaged",
  "Audience Transferred": "audience_transferred",
};

const ErrorState: React.FC = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 160,
      gap: 6,
    }}
  >
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="#F59E0B" strokeWidth="1.5" />
      <path
        d="M12 8v4.5"
        stroke="#F59E0B"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="12" cy="16" r="0.875" fill="#F59E0B" />
    </svg>
    <span
      style={{
        fontSize: 11,
        color: "#94a3b8",
        textAlign: "center",
        lineHeight: 1.5,
      }}
    >
      Data unavailable.
      <br />
      Please try refreshing.
    </span>
  </div>
);

const EmptyState: React.FC = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 160,
      gap: 6,
    }}
  >
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="#CBD5E1" strokeWidth="1.5" />
      <path
        d="M8 12h8"
        stroke="#CBD5E1"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
    <span
      style={{
        fontSize: 11,
        color: "#94a3b8",
        textAlign: "center",
        lineHeight: 1.5,
      }}
    >
      No data available for the selected period.
    </span>
  </div>
);

const PieChartCard: React.FC<PieChartCardProps> = ({
  title,
  subtitle,
  items,
  footerText,
  isLoading,
  hasError,
  onSegmentClick,
}) => {
  const [metric, setMetric] = useState<"total_count" | "query_count">(
    "total_count",
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeItems = items.map((item) => ({ ...item, value: item[metric] }));
  const totalValue = activeItems.reduce((sum, item) => sum + item.value, 0);
  const total = formatCount(totalValue);
  const selectedLabel =
    METRICS.find((m) => m.key === metric)?.label ?? "Select to plot";

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className={styles.card}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <span className={styles.title}>{title}</span>
          {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        </div>
        {/* <button className={styles.downloadBtn} title="Download">
          <img src={ChartDownloadIcon} width={26} height={26} alt="" />
        </button> */}
      </div>

      <div className={styles.divider} />

      {isLoading ? (
        <>
          <div className={styles.shimmerArea}>
            <div className={`${styles.shimmerBlock} ${styles.shimmerCircle}`} />
            <div className={styles.shimmerLegendCol}>
              {[80, 60, 70].map((w, i) => (
                <div
                  key={i}
                  className={`${styles.shimmerBlock} ${styles.shimmerLine}`}
                  style={{ width: w }}
                />
              ))}
            </div>
          </div>
          <div className={styles.shimmerFooter}>
            <div
              className={`${styles.shimmerBlock} ${styles.shimmerDropdown}`}
            />
          </div>
        </>
      ) : hasError ? (
        <ErrorState />
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* Chart + Legend */}
          <div className={styles.content}>
            <div className={styles.chartSide}>
              <div className={styles.chartWrapper}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={activeItems}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      innerRadius="52%"
                      outerRadius="78%"
                      paddingAngle={1}
                      startAngle={90}
                      endAngle={-270}
                      cursor={onSegmentClick ? "pointer" : undefined}
                      onClick={
                        onSegmentClick
                          ? (entry: any) => {
                              const key =
                                NAME_TO_EVENT_TYPE[entry.name] ?? entry.name;
                              onSegmentClick(key);
                            }
                          : undefined
                      }
                    >
                      {activeItems.map((item, index) => (
                        <Cell
                          key={index}
                          fill={item.color}
                          stroke="transparent"
                          strokeWidth={0}
                          style={
                            onSegmentClick ? { outline: "none" } : undefined
                          }
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className={styles.chartCenter}>
                  <span className={styles.totalValue}>{total}</span>
                  <span className={styles.totalLabel}>Total</span>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className={styles.legendSide}>
              {activeItems.map((item) => {
                const pct =
                  totalValue > 0
                    ? Math.round((item.value / totalValue) * 100)
                    : 0;
                const clickable = !!onSegmentClick;
                return (
                  <UiTooltip
                    key={item.name}
                    text={
                      <>
                        {item.value.toLocaleString()}
                        {clickable && <><br />Click to view daily breakdown</>}
                      </>
                    }
                    placement="top"
                  >
                  <div
                    className={styles.legendItem}
                    style={clickable ? { cursor: "pointer" } : undefined}
                    onClick={
                      clickable
                        ? () => {
                            const key =
                              NAME_TO_EVENT_TYPE[item.name] ?? item.name;
                            onSegmentClick!(key);
                          }
                        : undefined
                    }
                  >
                    <div
                      className={styles.legendDot}
                      style={{ backgroundColor: item.color }}
                    />
                    <span className={styles.legendName}>
                      <span
                        className={styles.legendNameText}
                        style={{ color: item.color }}
                      >
                        {item.name}
                      </span>
                    </span>
                    <span
                      className={styles.legendPct}
                      style={{ color: item.color }}
                    >
                      {pct}%
                    </span>
                  </div>
                  </UiTooltip>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <div className={styles.footerLeft}>
              <span className={styles.footerText}>{footerText ?? ""}</span>
              {onSegmentClick && (
                <span className={styles.drillHint}>
                  Click a segment to view daily breakdown
                </span>
              )}
            </div>
            <div className={styles.dropdownWrapper} ref={dropdownRef}>
              <button
                className={styles.dropdown}
                onClick={() => setDropdownOpen((o) => !o)}
              >
                <span>{selectedLabel}</span>
                <DownIcon width={10} height={6} style={{ color: "#092538" }} />
              </button>
              {dropdownOpen && (
                <div className={styles.dropdownMenu}>
                  {METRICS.map((m) => (
                    <button
                      key={m.key}
                      className={`${styles.dropdownMenuItem} ${metric === m.key ? styles.dropdownMenuItemActive : ""}`}
                      onClick={() => {
                        setMetric(m.key);
                        setDropdownOpen(false);
                      }}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PieChartCard;
